import { useQuery } from "@tanstack/react-query";
import { invalidateQueries } from "../../query-client";
import { BiometricTypeEnum } from "../../types/biometric-type-enum";
import { QueryKeys } from "../../types/query-key-enum";
import { getValueFromApp, isApp, setBiometricsToastShown } from "../../utils/app-helper";
import { PasswordlessContext } from "../passwordless-context";
import { useState } from "react";

type PasswordlessContextProviderProps = {
	children: React.ReactNode;
};

enum PasswordlessStatusEnum {
	Off,
	On,
}

const PasswordlessContextProvider = (props: PasswordlessContextProviderProps) => {
	const [isLoading, setIsLoading] = useState(false);

	const { data: biometricType } = useQuery({
		queryKey: ["GetBiometricType"],
		queryFn: () => getValueFromApp("GetBiometricType"),
		select(data) {
			return BiometricTypeEnum[data as keyof typeof BiometricTypeEnum];
		},
		enabled: isApp(),
	});

	const { data: showBiomatricToast } = useQuery({
		queryKey: ["CheckBiometricShown"],
		queryFn: () => getValueFromApp("CheckBiometricShown"),
		select(data) {
			return data.toLowerCase() === "false" ? true : false;
		},
		enabled: isApp() && biometricType !== BiometricTypeEnum.None,
	});

	const { data: passwordlessEnabled } = useQuery({
		queryKey: ["GetPasswordlessStatus"],
		queryFn: () => getValueFromApp("GetPasswordlessStatus"),
		select(data) {
			return (
				PasswordlessStatusEnum.On === PasswordlessStatusEnum[data as keyof typeof PasswordlessStatusEnum]
			);
		},
		enabled: isApp() && biometricType !== BiometricTypeEnum.None,
	});

	const setPasswordlessOn = () => {
		setIsLoading(true);

		return getValueFromApp("SetPasswordlessOn", undefined, { timeout: 30000 })
			.then((data: string) => {
				if (data.toLowerCase() === "off") {
					throw new Error("Failed setting passwordless");
				} else {
					invalidateQueries([["GetPasswordlessStatus"], [QueryKeys.Devices]]);
				}
				return true;
			})
			.catch((e: Error) => {
				invalidateQueries([["GetPasswordlessStatus"], [QueryKeys.Devices]]);
				throw e;
			})
			.finally(() => {
				setBiometricToastShown();
				setIsLoading(false);
			});
	};

	const setPasswordlessOff = () => {
		setIsLoading(true);

		getValueFromApp("SetPasswordlessOff", undefined, { timeout: 30000 })
			.then((data: string) => {
				if (data === "off") {
					throw new Error("Failed setting passwordless");
				} else {
					invalidateQueries([["GetPasswordlessStatus"], [QueryKeys.Devices]]);
				}
			})
			.catch((e: Error) => {
				invalidateQueries([["GetPasswordlessStatus"], [QueryKeys.Devices]]);
				throw e;
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const setBiometricToastShown = () => {
		setBiometricsToastShown();
		invalidateQueries([["CheckBiometricShown"]]);
	};

	return (
		<PasswordlessContext.Provider
			value={{
				showBiomatricToast,
				biometricType,
				passwordlessEnabled: passwordlessEnabled ?? false,
				setShowBiometricToast: setBiometricToastShown,
				setPasswordlessOn: setPasswordlessOn,
				setPasswordlessOff: setPasswordlessOff,
				isLoading,
			}}
		>
			{props.children}
		</PasswordlessContext.Provider>
	);
};

export default PasswordlessContextProvider;
